import React, { useContext } from "react";
import { IconGlobe } from "@web-monorepo/mds";
import { ModalContext, ModalType } from "@src/components/modals/ModalController";
import { TranslationContext } from "@src/components/translation/TranslationContext";
import { fixLocale } from "@src/utils/routes";
import { logEvent } from "@src/utils/logClient";
import window from "global/window";

// Convert a 2-letter country code to a flag emoji using regional indicator symbols
const countryCodeToFlag = (countryCode: string): string => {
  const upperCode = countryCode.toUpperCase();
  const codePoints = [...upperCode].map((char) => 0x1f1e6 + char.charCodeAt(0) - 65);
  return String.fromCodePoint(...codePoints);
};

// Special cases where locale country code doesn't map to a real flag
const countryCodeFlagOverrides: Record<string, string> = {
  AR: "SA", // ar-AR (Arabic) -> Saudi Arabia flag
};

const getFlagEmoji = (locale: string): string => {
  const fixed = fixLocale(locale);
  const parts = fixed.split("-");
  const countryCode = parts[1]?.toUpperCase();
  if (!countryCode) {
    return "🌐";
  }
  const finalCode = countryCodeFlagOverrides[countryCode] ?? countryCode;
  return countryCodeToFlag(finalCode);
};

type HeaderLanguagePickerProps = {
  variant?: "desktop" | "mobile";
  // Only meaningful for the desktop variant — the mobile menu panel is always on a white
  // background, so it never needs the light-on-transparent treatment.
  light?: boolean;
};

const HeaderLanguagePicker: React.FC<HeaderLanguagePickerProps> = ({ variant = "desktop", light }) => {
  const { showModal } = useContext(ModalContext);
  const { translate, translationLocaleAsString } = useContext(TranslationContext);

  const currentLocale = translationLocaleAsString?.() || "en-US";
  const flagEmoji = getFlagEmoji(currentLocale);

  const handleClick = () => {
    logEvent({
      eventName: "web.external_page.navbar.language_picker.click",
      metadata: {
        variant,
      },
    });
    showModal(ModalType.LanguageChooser);
  };

  const selectLanguageLabel = translate("components.signup_modal.title") as string;

  if (variant === "mobile") {
    return (
      <button
        onClick={handleClick}
        aria-label={selectLanguageLabel}
        className="flex items-center gap-x-8 py-8 transition-colors cursor-pointer"
      >
        <IconGlobe size="m" />
        <span className="flex-1 text-ginto-18 font-normal leading-130 text-left">{selectLanguageLabel}</span>
      </button>
    );
  }

  return (
    <button
      onClick={handleClick}
      aria-label={selectLanguageLabel}
      className={`flex items-center gap-x-4 px-8 py-4 rounded-full hover:bg-surface-secondary transition-colors cursor-pointer ${
        light ? "text-content-light" : ""
      }`}
    >
      <IconGlobe size="s" />
      <span className="text-xl leading-none">{flagEmoji}</span>
    </button>
  );
};

export default HeaderLanguagePicker;
